/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.15.0
 */

import { useCallback, useMemo } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Button, LinkBlock } from '../../../components/common';
import { ImgStatusSuccess } from '../../../components/common/Block/Img';
import { ELocaleKeys } from '../../../modules/internationalization/intConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardBillsDoc,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation, IWizardBillsDocTranslation } from '../../../modules/internationalization/types';
import Modal from '../../../modules/modal/components/Modal';
import { ModalCenterStyled } from '../../../modules/modal/components/styled/ModalCenterStyled';
import { IModalRenderProps } from '../../../modules/modal/types/modalTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { RoutePath } from '../../../routes/constants/routesConstants';
import { stringFormatParams } from '../../../utils/stringFormat';
import { IWizardBillsDocOrderResult } from '../types/wizardBillsDocTypes';
import { wizardBillsDocOrderResultHasError } from '../utils/wizardBillsDocOrderResult';
import { WizardBillsDocModalOrderResultStyled } from './styled/WizardBillsDocModalOrderResultStyled';

interface IWizardBillsDocModalOrderResult extends Pick<IModalRenderProps, 'onCloseModal'> {
    orderResult: IWizardBillsDocOrderResult;
}

const WizardBillsDocModalOrderResult = ({
                                            onCloseModal,
                                            orderResult,
                                            translationCommon,
                                            translationBillsDoc,
                                        }: IWizardBillsDocModalOrderResult & IConnectedProps) => {

    const t = useMemo(() => translationBillsDoc.summaryModal, [translationBillsDoc]);

    const successOption = useCallback((count: number, label: string): string | undefined =>
        count ? stringFormatParams(label, { [ELocaleKeys.number]: count }) : undefined, []);

    const failureOption = useCallback((count: number, account?: string, label?: string): string | undefined =>
        (count && account) ? stringFormatParams(label, { [ELocaleKeys.string]: account }) : undefined, []);

    return (
        <Modal dataName="bills-doc-modal-order" width="590px">
            <WizardBillsDocModalOrderResultStyled>
                <ModalCenterStyled>
                    <ImgStatusSuccess/>
                    <Modal.Title>
                        {t.title}
                    </Modal.Title>
                    <Modal.Text>
                        <div className="success-container">
                            {stringFormatParams(t.formedSuccess.title, {
                                [ELocaleKeys.string]: [
                                    successOption(orderResult.actsCount, t.formedSuccess.acts),
                                    successOption(orderResult.billsCount, t.formedSuccess.bills),
                                    successOption(orderResult.fuctsCount, t.formedSuccess.fucts),
                                    successOption(orderResult.invoicesCount, t.formedSuccess.invoices),
                                ].filter(value => !!value).join(', '),
                            })}
                        </div>
                    </Modal.Text>
                    {
                        wizardBillsDocOrderResultHasError(orderResult) && (
                            <Modal.Text>
                                <div className="error-container">
                                    {
                                        [
                                            failureOption(orderResult.actsErrorCount,
                                                orderResult.actErrorAccount, t.formedFailure.acts),
                                            failureOption(orderResult.billsErrorCount,
                                                orderResult.billErrorAccount, t.formedFailure.bills),
                                            failureOption(orderResult.fuctsErrorCount,
                                                orderResult.fuctErrorAccount, t.formedFailure.fucts),
                                            failureOption(orderResult.invoicesErrorCount,
                                                orderResult.invoicesErrorAccount, t.formedFailure.invoices),
                                        ].filter(value => !!value).map(value => <div>{value}</div>)
                                    }
                                </div>
                            </Modal.Text>
                        )
                    }
                    <Modal.Text>
                        {translationCommon.modalOrder.details}&nbsp;
                        <LinkBlock url={RoutePath.orders}>
                            «{translationCommon.pages.historyOrder}»
                        </LinkBlock>
                    </Modal.Text>
                    <Modal.Control>
                        <Button
                            dataName="bills-doc-modal-order-ok"
                            kind="medium"
                            onClick={onCloseModal}
                        >
                            {translationCommon.modal.good}
                        </Button>
                    </Modal.Control>
                </ModalCenterStyled>
            </WizardBillsDocModalOrderResultStyled>

        </Modal>
    );
};

interface IConnectedProps {
    translationBillsDoc: IWizardBillsDocTranslation;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    translationCommon: makeSelectTranslationCommon(),
    translationBillsDoc: makeSelectTranslationWizardBillsDoc(),
});

export default connect(mapStateToProps)(WizardBillsDocModalOrderResult);
